import { z } from 'zod'
import { CONTRIBUTION_SOURCES, CONTRIBUTION_STATUSES } from '@/types/space-contributions'

/**
 * Zod schema for space contribution form validation
 */
export const spaceContributionFormSchema = z
  .object({
    // Required identification fields
    unit_id: z.string().min(1, 'Unit is required'),
    source_type: z.enum(CONTRIBUTION_SOURCES as [string, ...string[]], {
      errorMap: () => ({ message: 'Source type is required' }),
    }),

    // Required datetime fields
    available_from: z
      .string()
      .min(1, 'Available from date is required')
      .refine((val) => !isNaN(Date.parse(val)), 'Invalid date format'),
    available_until: z
      .string()
      .min(1, 'Available until date is required')
      .refine((val) => !isNaN(Date.parse(val)), 'Invalid date format'),

    // Required space fields
    sqft: z.coerce
      .number()
      .positive('Square feet must be positive')
      .refine((val) => val > 0, 'Square feet is required'),

    // Optional dimension fields
    wingspan: z
      .union([z.coerce.number().positive('Wingspan must be positive'), z.literal('')])
      .transform((val) => (val === '' ? undefined : val))
      .optional(),
    length: z
      .union([z.coerce.number().positive('Length must be positive'), z.literal('')])
      .transform((val) => (val === '' ? undefined : val))
      .optional(),

    // Optional position fields
    position_x: z
      .union([z.coerce.number(), z.literal('')])
      .transform((val) => (val === '' ? undefined : val))
      .optional(),
    position_y: z
      .union([z.coerce.number(), z.literal('')])
      .transform((val) => (val === '' ? undefined : val))
      .optional(),

    // Optional relationship fields
    aircraft_id: z.string().optional().or(z.literal(null)),
    flight_schedule_id: z.string().optional().or(z.literal(null)),
    lease_contract_id: z.string().optional().or(z.literal(null)),

    // Status
    status: z.enum(CONTRIBUTION_STATUSES as [string, ...string[]], {
      errorMap: () => ({ message: 'Status is required' }),
    }),
  })
  .refine(
    (data) => {
      const from = new Date(data.available_from)
      const until = new Date(data.available_until)
      return from < until
    },
    {
      message: 'Available until must be after available from',
      path: ['available_until'],
    }
  )
  .refine(
    (data) => {
      // If source_type is aircraft_away, aircraft_id is required
      if (data.source_type === 'aircraft_away' && !data.aircraft_id) {
        return false
      }
      return true
    },
    {
      message: 'Aircraft is required when source type is "Aircraft Away"',
      path: ['aircraft_id'],
    }
  )

// Input type for form defaultValues
export type SpaceContributionFormInput = z.input<typeof spaceContributionFormSchema>

// Output type after validation/parsing
export type SpaceContributionFormValues = z.output<typeof spaceContributionFormSchema>
